import type { Language } from '../../../../common/types/i18n.js'
import type { NotionProject, NotionProjectMember } from '../../../../common/types/notion.js'

/**
 * Pure helpers for the Projects (ex-worktime) view — extracted from the model
 * so they can be unit-tested without pulling in the view layer.
 */

export const groupProjectsByYear = (projects: NotionProject[]): Map<string, NotionProject[]> => {
    const currentYear = new Date().getFullYear()
    const projectsByYear = new Map<string, NotionProject[]>()

    for (const project of projects) {
        const startYear = project.startDate
            ? new Date(project.startDate).getFullYear()
            : currentYear
        const endYear = project.endDate ? new Date(project.endDate).getFullYear() : currentYear

        for (let year = startYear; year <= endYear; year++) {
            const yearStr = year.toString()
            if (!projectsByYear.has(yearStr)) {
                projectsByYear.set(yearStr, [])
            }
            projectsByYear.get(yearStr)!.push(project)
        }
    }

    return projectsByYear
}

/**
 * "Minu projektid": projects where the person is the Owner or in the Team.
 * Primary match is the server-enriched portalUserId (which falls back to an
 * unambiguous full-name match — Notion account emails don't always equal the
 * portal login email, e.g. a play.ee login with a nope.ee Notion account);
 * the raw email comparison stays as a secondary check.
 */
export const filterMyProjects = (
    projects: NotionProject[],
    userEmail: string | null,
    userId: number | null,
): NotionProject[] => {
    if (!userEmail && userId === null) return []
    const email = userEmail?.toLowerCase() ?? null

    const isMe = (member: NotionProjectMember): boolean =>
        (userId !== null && member.portalUserId === userId) ||
        (email !== null && member.email.toLowerCase() === email)

    return projects.filter(
        (p) => (p.owner !== null && isMe(p.owner)) || p.team.some(isMe),
    )
}

export const applySearch = (projects: NotionProject[], query: string): NotionProject[] => {
    if (!query.trim()) return projects
    const q = query.toLowerCase()
    return projects.filter(
        (p) =>
            p.name.toLowerCase().includes(q) ||
            p.status.toLowerCase().includes(q) ||
            p.team.some((m) => m.name.toLowerCase().includes(q)),
    )
}

const formatMonthYear = (date: string, lang: Language): string =>
    new Date(date).toLocaleDateString(lang === 'et' ? 'et-EE' : 'en-GB', {
        month: 'short',
        year: 'numeric',
    })

/**
 * Card-level date label at month precision in the given language. A range
 * within one month collapses to a single label ("May 2026", not
 * "May 2026 – May 2026"); an open-ended project shows "May 2026 – ...".
 */
export const formatProjectCardDate = (
    start: string | null,
    end: string | null,
    lang: Language,
): string => {
    if (!start && !end) return ''
    if (!start) return formatMonthYear(end!, lang)
    const startLabel = formatMonthYear(start, lang)
    if (!end) return `${startLabel} – ...`
    const endLabel = formatMonthYear(end, lang)
    return startLabel === endLabel ? startLabel : `${startLabel} – ${endLabel}`
}
